import {groupBy, groupByUniqueKey} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {CodonTranslationTable, NucleotideType} from 'biojs/BioConstants';
import {TranslationGroup} from 'biojs/Sequence';
import {Model as BioJSTranslation, TranslationAttributes} from 'biojs/Translation';
import * as bioUtil from 'biojs/Util';
import {Sequence} from 'components/Sequence';
import {CustomField, Strand} from 'constants/MolbioConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {TRANSLATION_MAX_NUM_CUSTOM_FIELDS, TRANSLATION_MAX_NAME_LENGTH} from 'constants/SequenceConstants';
import {pastelBlue} from 'lib/Colors';
import {isRangeOverlap, modelEndToDisplay, modelStartToDisplay, residueSlice} from 'molbio/SeqUtil';
import Translation from 'molbio/translations/backbone/Translation';
import TranslationLinkInvalidationWarningModal from 'molbio/translations/TranslationLinkInvalidationWarningModal';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import {FeatureDisplayData} from 'toolbar/feature-panel/FeatureRowItem';
import legacyRunModal from 'ui-library/legacyRunModal';
import sortedBy from 'util/sortedBy';

export const TRANSLATION_EDIT_FIELDS: Array<
  'name' | 'notes' | 'strand' | 'codonTranslationTable' | 'color' | 'coerceStartCodonToMethionine'
> = ['name', 'notes', 'strand', 'codonTranslationTable', 'color', 'coerceStartCodonToMethionine'];

export const DEFAULT_TRANSLATION_COLOR = pastelBlue;

/**
 * Translate a set of regions into an amino acid sequence.
 *
 * Extracts bases from each region in order (handling circular sequences),
 * concatenates them to form the complete coding sequence, and translates
 * using the specified genetic code and strand. The resulting amino acid
 * sequence may contain stop codons (*), which can be optionally truncated
 * at the first stop codon. For example:
 * - "ATGCCCGGGCCCTAA" (on forward strand) → "MPGP*" (or "MPGP" with truncateBeforeStop)
 * - "ATGTAACCCTAGGGG" → "M*P*G" (or "M" with truncateBeforeStop)
 *
 * IMPORTANT: Regions with null start or end are automatically skipped (cannot be translated).
 * Valid regions (with non-null boundaries) must be:
 * - Non-overlapping: No two regions can share the same base positions
 * - Ordered by position: Regions must be sorted by start position
 * - Circular-ready: Each region's start/end already adjusted for circular sequences
 *
 * @param regions - Array of regions with start/end positions (0-indexed, may be non-contiguous); regions with null boundaries are skipped
 * @param sequenceBases - The full DNA/RNA sequence
 * @param strand - Forward (1) or reverse (-1) strand
 * @param nucleotideType - DNA or RNA
 * @param codonTranslationTable - Genetic code to use
 * @param coerceStartCodonToMethionine - Whether to force first codon to translate as Met
 * @param truncateBeforeStop - If true, truncate the sequence before the first stop codon
 * @returns Amino acid sequence string (may contain stop codons as *, unless truncateBeforeStop is true)
 */
export function translateRegions(
  regions: Array<{start: number | null; end: number | null}>,
  sequenceBases: string,
  strand: Strand,
  nucleotideType: NucleotideType,
  codonTranslationTable: CodonTranslationTable,
  coerceStartCodonToMethionine: boolean,
  truncateBeforeStop: boolean = false
): string {
  if (regions.length === 0) {
    return '';
  }

  const validRegions = regions.filter(
    (r): r is {start: number; end: number} => r.start != null && r.end != null
  );

  if (validRegions.length === 0) {
    return '';
  }

  const concatenatedBases = validRegions
    .map(({start, end}) => residueSlice(sequenceBases, start, end))
    .join('');

  if (concatenatedBases.length === 0) {
    return '';
  }

  // Translate the concatenated bases
  let aminoAcids = bioUtil.translateOnStrand(
    concatenatedBases,
    strand,
    nucleotideType,
    codonTranslationTable,
    coerceStartCodonToMethionine
  );

  if (truncateBeforeStop) {
    const stopIndex = aminoAcids.indexOf('*');
    if (stopIndex !== -1) {
      aminoAcids = aminoAcids.substring(0, stopIndex);
    }
  }

  return aminoAcids;
}

/**
 * Count the number of amino acids in a sequence, excluding stop codons.
 *
 * Stop codons (*) are not counted as amino acids. For example:
 * - "AR*DV*K" contains 5 amino acids (A, R, D, V, K)
 * - "MPGP*" contains 4 amino acids (M, P, G, P)
 * - "M*PG" contains 3 amino acids (M, P, G)
 * - "***" contains 0 amino acids
 *
 * @param aminoAcidSequence - Amino acid sequence (may contain stop codons as *)
 * @returns Number of amino acids (stop codons not counted)
 */
export function countAminoAcids(aminoAcidSequence: string): number {
  return aminoAcidSequence.replace(/\*/g, '').length;
}

export interface TranslationRegionData {
  id: number;
  cid: string;
  start: number;
  end: number;
  strand: Strand;
}

/**
 Translations in a region group are in position order.
 - A translation group represents a translation with multiple regions
 - A lone translation without a group represents a translation with a single region
 */
export interface TranslationRegionGroupData extends FeatureDisplayData {
  translations: Array<TranslationRegionData>;
  aminoAcidLength: number;
  notes: string;
  editable: boolean;
  strand: Strand;
  customFields: Array<CustomField>;
  hasGroup: boolean;
  codonTranslationTable: CodonTranslationTable;
  coerceStartCodonToMethionine: boolean;
}

function translationToData(translation: Translation): TranslationRegionData {
  return {
    id: translation.id,
    cid: translation.cid,
    start: translation.start(),
    end: translation.end(),
    strand: translation.strand(),
  };
}

function toTranslationRegionGroupData(
  translations: Array<Translation>,
  group: TranslationGroup | undefined,
  sequence: Sequence
): TranslationRegionGroupData {
  return {
    translations: sortedBy(
      translations.map((t) => translationToData(t)),
      ['start']
    ),
    aminoAcidLength: countAminoAcids(
      translateRegions(
        translations.map((t) => ({start: t.start(), end: t.end()})),
        sequence.bases(),
        translations[0].strand(),
        sequence.nucleotideType(),
        group?.codonTranslationTable ?? CodonTranslationTable.STANDARD,
        group?.coerceStartCodonToMethionine ?? false
      )
    ),
    name: group?.name ?? '',
    notes: group?.notes ?? '',
    customFields: group?.customFields ?? [],
    editable: translations.every((t) => t.editable()),
    selected: translations.some((t) => t.selected()),
    strand: translations[0].strand(),
    cid: group?.id.toString() ?? translations[0].cid,
    hasGroup: Boolean(group),
    codonTranslationTable: group?.codonTranslationTable ?? CodonTranslationTable.STANDARD,
    coerceStartCodonToMethionine: group?.coerceStartCodonToMethionine ?? false,
    color: group?.color ?? DEFAULT_TRANSLATION_COLOR,
  };
}

/**
 * Gets the translation region group (see: TranslationRegionGroupData) for the given translation.
 */
export function getTranslationRegionGroupForTranslation(
  sequence: Sequence,
  translation: Translation
): TranslationRegionGroupData {
  const group = translation.translationGroupId()
    ? sequence.translationGroups().find((g) => g.id === translation.translationGroupId())
    : undefined;
  return toTranslationRegionGroupData(translation.groupTranslations(), group, sequence);
}

/**
 * Gets all the translation region groups (see: TranslationRegionGroupData) for a given sequence.
 *
 * This can be somewhat expensive for large sequences with lots of translations,
 * so `getTranslationRegionGroupForTranslation` above should be used when working with a specific group.
 */
export function getTranslationRegionGroupsForSequence(sequence: Sequence): Array<TranslationRegionGroupData> {
  const translationsByGroupId: {[id: string]: Array<Translation>} = groupBy(
    sequence.translations().models,
    (translation) => translation.translationGroupId() ?? translation.cid
  );
  const translationGroupsById: {[id: string]: TranslationGroup} = groupByUniqueKey(
    sequence.translationGroups(),
    (g) => g.id
  );

  return Object.entries(translationsByGroupId).map(([groupId, translations]) =>
    toTranslationRegionGroupData(translations, translationGroupsById[groupId], sequence)
  );
}

// After successfully saving changes to a translation, update the changed Backbone models.
// updatedGroups and regions represent the new state of any new or modified translation groups
// and their translation regions
export function updateModelsAfterTranslationChange(
  sequence: Sequence,
  updatedGroups: Array<TranslationGroup>,
  regions: Array<BioJSTranslation | TranslationAttributes>,
  regionsToDeselect: Array<Translation>
): void {
  const updatedGroupIds = updatedGroups.map(({id}) => id);
  const newGroups = [
    ...sequence.translationGroups().filter(({id}) => !updatedGroupIds.includes(id)),
    ...updatedGroups,
  ];
  sequence._sequence().set({translationGroups: newGroups});
  const updatedRegionIds = regions.map(({id}) => id);
  const currentRegions = sequence._sequence().translations().toArray();
  // Filter out any translations that were part of modified groups but no longer in the updated regions.
  // These translations have been deleted
  const remainingRegions = currentRegions.filter((translation) => {
    const removedFromUpdatedGroup =
      updatedGroupIds.includes(translation.translationGroupId()!) &&
      !updatedRegionIds.includes(translation.id);
    return !removedFromUpdatedGroup;
  });
  const updatedRegions = [...remainingRegions, ...regions];
  // @ts-expect-error: There are missing types, but Backbone handles parsing models from attributes automatically
  sequence._sequence().translations().set(updatedRegions);
  for (const region of regionsToDeselect) {
    region.set({selected: false});
  }
  sequence.updateVersions();
}

export function doesRegionOverlapGroup(
  group: TranslationRegionGroupData,
  regionStart: number | null,
  regionEnd: number | null
): boolean {
  return (
    regionStart != null &&
    regionEnd != null &&
    group.translations.some(({start, end}) => isRangeOverlap(start, end, regionStart, regionEnd))
  );
}

interface TranslationFormAttrs {
  name: string;
  translationRegionGroup: TranslationRegionGroupData | null;
  isCreateActive: boolean;
  start: number | null;
  end: number | null;
  customFields: Array<CustomField>;
}

export function validateTranslationForm(
  attrs: TranslationFormAttrs,
  sequenceStartIndex: number,
  sequenceLength: number,
  isCircularSequence: boolean
): string | null {
  // The translation notes textbox already limits max length and does not need to be validated here
  if (attrs.name.length > TRANSLATION_MAX_NAME_LENGTH) {
    return 'Translation name is too long';
  } else if (attrs.customFields.length > TRANSLATION_MAX_NUM_CUSTOM_FIELDS) {
    return 'Translation has too many custom fields';
  } else if (attrs.isCreateActive) {
    // start and end are only applicable to creating a new translation region.
    // Location cannot be changed when editing an existing translation.
    const {start, end} = attrs;
    if (start == null || end == null) {
      return 'A translation must have a valid start and end';
    } else if (sequenceLength === 0) {
      return 'A translation cannot be added to 0-length sequence';
    } else if (!(0 <= start && start < sequenceLength && 0 <= end && end < sequenceLength)) {
      return `Start and end values must be between ${sequenceStartIndex} and ${
        sequenceStartIndex - 1 + sequenceLength
      }.`;
    } else if (!isCircularSequence && end !== 0 && end < start) {
      return 'End must be greater than start on a linear sequence';
    } else if (start === end && end !== 0) {
      return 'Region must have a length greater than 0';
    } else if (
      attrs.translationRegionGroup != null &&
      doesRegionOverlapGroup(attrs.translationRegionGroup, start, end)
    ) {
      return 'Translation regions cannot overlap';
    } else {
      return null;
    }
  } else {
    return null;
  }
}

// Gets the name of a translation group. If the name is empty, use the positions of the group instead.
export function getTranslationNameOrPlaceholder(
  group: TranslationRegionGroupData,
  sequenceStartIndex: number,
  sequenceLength: number
): string {
  assert(group.name != null, `Expected translation name to exist for translation ${group.cid}`);
  if (group.name?.length > 0) {
    return group.name;
  } else {
    const {translations} = group;
    const modelStart = translations[0].start;
    const modelEnd = translations[translations.length - 1].end;
    return `${modelStartToDisplay(modelStart, sequenceStartIndex)}-${modelEndToDisplay(
      modelEnd,
      sequenceLength,
      sequenceStartIndex
    )}`;
  }
}

export function getTranslationRegionFormattedName(
  translationRegionGroup: TranslationRegionGroupData,
  translationId: number,
  sequenceStartIndex: number,
  sequenceLength: number
): string {
  const translationRegionIndex = translationRegionGroup.translations.findIndex(
    (translationRegion) => translationRegion.id === translationId
  );
  assert(translationRegionIndex >= 0, 'Unexpectedly could not find translation in translation group');

  const translationGroupName = getTranslationNameOrPlaceholder(
    translationRegionGroup,
    sequenceStartIndex,
    sequenceLength
  );
  // Only show the region number if there are multiple regions in the translation
  return translationRegionGroup.translations.length === 1
    ? translationGroupName
    : `${translationGroupName}: Region ${translationRegionIndex + 1}`;
}

/*
 * If necessary, launch a confirmation modal and confirm the user's intent to edit or delete
 * a translation. In some cases, such an action can invalidate translation links which can be surprising.
 */
export async function confirmTranslationLinkInvalidationRisk(
  tagSchema: TagSchema | undefined,
  affectsCodonTranslationTable: boolean,
  affectsCoerceStartCodonToMethionine: boolean,
  isDeletion: boolean,
  count: number
): Promise<boolean> {
  if (
    tagSchema != null &&
    tagSchema.fields.some((f) => f.fieldType === FIELD_TYPES.TRANSLATION_LINK.type) &&
    (affectsCodonTranslationTable || affectsCoerceStartCodonToMethionine)
  ) {
    const shouldContinue = await legacyRunModal(TranslationLinkInvalidationWarningModal, {
      isDeletion,
      count,
    });
    return Boolean(shouldContinue);
  }
  return true;
}
